
import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export type GradientVariant = 'blue' | 'green' | 'red' | 'purple' | 'amber' | 'default' | 'destructive' | undefined;

// Remove all animation and drag-related event props to avoid conflicts between React and Framer Motion
type MotionButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>, 
  | 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onDragExit'
  | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
>;

export interface GradientButtonProps extends MotionButtonProps {
  variant?: GradientVariant;
  icon?: React.ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
  testId?: string;
}

/**
 * GradientButton component with consistent styling and animations
 * Used for primary actions with gradient backgrounds
 */
const GradientButton = React.forwardRef<HTMLButtonElement, GradientButtonProps>(
  ({ 
    children, 
    variant = 'blue', 
    icon, 
    isLoading = false,
    fullWidth = true,
    className,
    disabled,
    testId,
    ...props 
  }, ref) => {
    // Gradient variants - all going from light (left) to dark (right)
    const gradients = {
      blue: 'bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700',
      green: 'bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700',
      red: 'bg-gradient-to-r from-red-400 to-red-600 hover:from-red-500 hover:to-red-700',
      purple: 'bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700',
      amber: 'bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700',
      default: 'bg-gradient-to-r from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700',
      destructive: 'bg-gradient-to-r from-red-400 to-red-600 hover:from-red-500 hover:to-red-700',
      undefined: '', // For when no variant is provided
    };

    // Animation props for the motion button
    const motionProps = {
      whileHover: (!disabled && !isLoading) ? { scale: 1.02 } : undefined,
      whileTap: (!disabled && !isLoading) ? { scale: 0.98 } : undefined,
    };

    return (
      <motion.button
        ref={ref}
        {...motionProps}
        disabled={disabled || isLoading}
        data-test-id={testId}
        className={cn(
          // Base styles
          'h-10 py-2 text-sm font-medium rounded-lg',
          'transition-all duration-200',
          'flex items-center gap-2 pl-5 pr-6',
          'shadow-sm hover:shadow-md',
          // Text color based on variant
          variant ? 'text-white' : 'text-gray-800',
          // Background when no variant
          !variant && 'bg-white border border-gray-200 hover:bg-gray-50',
          // Width
          fullWidth ? 'w-full' : 'w-auto',
          // Gradient variant (only apply if variant is provided)
          variant ? gradients[variant] : '',
          // Disabled state
          (disabled || isLoading) && 'opacity-70 cursor-not-allowed hover:scale-100',
          // Custom classes
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{typeof children === 'string' ? 'Loading...' : children}</span>
          </>
        ) : (
          <>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span>{children}</span>
          </>
        )}
      </motion.button>
    );
  }
);

GradientButton.displayName = 'GradientButton';

export default GradientButton;
